import { ErrorDeLaApi, mensajePorEstado, pedir, recuperarLaSesion, consulta } from './nucleo'

/**
 * La subida por lotes de la biblioteca (plan del CMS dinámico §13), del lado del panel: se crea un
 * lote con lo que se va a subir (nombre, tamaño y tipo), y los ficheros se suben **de tres en tres**,
 * cada uno en su petición, con su progreso. El análisis (tipo de verdad, antivirus, tamaños) lo hace
 * el núcleo en su cola; el panel pregunta por el lote para enseñar cómo va cada fichero.
 *
 * Aquí está lo que no es React —la cola, el casado de ficheros al reanudar, el estado de cada
 * fichero— para poder probarlo sin navegador.
 */
export type Visibilidad = 'privado' | 'publico'

export type Anunciado = { nombre: string; tamano: number; tipo: string }

export type LoteDelNucleo = {
  id: number
  cliente: number
  sitio: number
  estado: 'cancelado' | 'pausado' | 'pendiente' | 'procesando' | 'subiendo' | 'terminado'
  total: number
  hechos: number
  fallidos: number
  bloqueados: number
  duplicados: number
  visibilidad: Visibilidad
  carpeta: null | string
  subirIgualmente: boolean
  ficheros: Anunciado[]
  createdAt: string
  /** Solo en la lista de abiertos: cuántos no llegaron a subirse. */
  sinSubir?: number
}

/** Lo que dice el núcleo de cada fichero del lote. `pendiente`: todavía no se ha subido. */
export type EstadoEnElNucleo = 'analizando' | 'bloqueado' | 'duplicado' | 'fallido' | 'listo' | 'pendiente' | 'procesando' | 'subiendo'

export type FicheroDelNucleo = { indice: number; nombre: string; tamano: number; estado: EstadoEnElNucleo; error: null | string; medio: null | number; url: null | string; tipo: null | string }

export type ProgresoDelLote = { lote: LoteDelNucleo; ficheros: FicheroDelNucleo[]; pendientes: number }

/** Lo que ve la persona: lo del núcleo, más lo que solo sabe el navegador (que está reduciendo o subiendo, y cuánto lleva). */
export type EstadoDeFichero = EstadoEnElNucleo | 'cancelado' | 'reduciendo'

export type FicheroEnPantalla = { indice: number; nombre: string; tamano: number; estado: EstadoDeFichero; progreso: number; motivo: null | string; medio: null | number; url: null | string }

/** Ya no va a cambiar: ni se sube, ni se analiza. */
export const terminado = (e: EstadoDeFichero): boolean => e === 'listo' || e === 'fallido' || e === 'bloqueado' || e === 'duplicado' || e === 'cancelado'

export const loteAbierto = (l: Pick<LoteDelNucleo, 'estado'>): boolean => l.estado === 'pendiente' || l.estado === 'subiendo' || l.estado === 'procesando' || l.estado === 'pausado'

/** Cuántos subir a la vez desde el navegador (§13.1). */
export const A_LA_VEZ = 3

export const crearLote = (o: { sitio: number; ficheros: Array<{ name: string; size: number; type: string }>; visibilidad: Visibilidad; carpeta?: string; subirIgualmente?: boolean }) =>
  pedir<{ lote: LoteDelNucleo }>('/medios/lotes', {
    metodo: 'POST',
    cuerpo: {
      sitio: o.sitio,
      visibilidad: o.visibilidad,
      carpeta: o.carpeta || undefined,
      subirIgualmente: o.subirIgualmente === true,
      ficheros: o.ficheros.map((f) => ({ nombre: f.name, tamano: f.size, tipo: f.type || '' })),
    },
  })

export const verLote = (lote: number) => pedir<ProgresoDelLote>(`/medios/lotes/${lote}`)

export const lotesAbiertos = (sitio: number) => pedir<{ lotes: LoteDelNucleo[] }>(`/medios/lotes${consulta({ sitio, abiertos: 1 })}`)

export const cambiarLote = (lote: number, accion: 'cancelar' | 'pausar' | 'reanudar') => pedir<ProgresoDelLote>(`/medios/lotes/${lote}/${accion}`, { metodo: 'POST' })

/** Una petición `PUT` con su progreso de subida (`fetch` no lo da). Un 401 recupera la sesión y repite una vez. */
const putConProgreso = (url: string, fichero: Blob, cabeceras: Record<string, string>, alProgreso: (fraccion: number) => void, senal?: AbortSignal): Promise<{ status: number; json: Record<string, unknown> }> =>
  new Promise((resolver, rechazar) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.withCredentials = true
    for (const [k, v] of Object.entries(cabeceras)) xhr.setRequestHeader(k, v)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) alProgreso(e.loaded / e.total)
    }
    xhr.onload = () => {
      let json: Record<string, unknown> = {}
      try {
        json = xhr.responseText ? (JSON.parse(xhr.responseText) as Record<string, unknown>) : {}
      } catch {
        json = {}
      }
      resolver({ status: xhr.status, json })
    }
    xhr.onerror = () => rechazar(new ErrorDeLaApi('Se ha cortado la conexión mientras se subía. Vuelve a intentarlo.', 0))
    xhr.onabort = () => rechazar(new DOMException('Subida cancelada', 'AbortError'))
    senal?.addEventListener('abort', () => xhr.abort(), { once: true })
    xhr.send(fichero)
  })

const mensajeDe = (json: Record<string, unknown>, status: number): string => {
  const errores = Array.isArray(json.errors) ? (json.errors as Array<{ message?: string }>) : []
  return errores[0]?.message || (typeof json.mensaje === 'string' ? json.mensaje : '') || mensajePorEstado(status)
}

/** Sube el fichero `n` de un lote, en el cuerpo tal cual (no multipart): el núcleo lo pasa en flujo a «entrantes». */
export const subirFichero = async (lote: number, n: number, fichero: File | Blob, nombre: string, alProgreso: (f: number) => void, senal?: AbortSignal): Promise<{ medio: number }> => {
  const cabeceras = { 'Content-Type': fichero.type || 'application/octet-stream', 'X-Nombre': encodeURIComponent(nombre) }
  const url = `/api/medios/lotes/${lote}/ficheros/${n}`
  let r = await putConProgreso(url, fichero, cabeceras, alProgreso, senal)
  if (r.status === 401 && (await recuperarLaSesion())) r = await putConProgreso(url, fichero, cabeceras, alProgreso, senal)
  if (r.status < 200 || r.status >= 300) throw new ErrorDeLaApi(mensajeDe(r.json, r.status), r.status, (r.json.errors as Array<{ data?: Record<string, unknown> }> | undefined)?.[0]?.data ?? {})
  return { medio: Number((r.json.medio as { id?: unknown } | undefined)?.id) }
}

/**
 * Al volver con la pestaña cerrada, el navegador ya no tiene los ficheros: se piden otra vez y se
 * casan con los puestos que faltan **por nombre y tamaño** (los de antes de reducir, si se
 * redujeron, no casarán: el tamaño anunciado es el reducido, y se avisa). Cada elegido sirve para
 * un puesto, el primero que case.
 */
export const casarFicheros = <F extends { name: string; size: number }>(anunciados: Anunciado[], faltan: number[], elegidos: F[]): { casados: Map<number, F>; sinCasar: F[] } => {
  const casados = new Map<number, F>()
  const libres = [...elegidos]
  for (const indice of faltan) {
    const a = anunciados[indice]
    if (!a) continue
    const i = libres.findIndex((f) => f.name === a.nombre && f.size === a.tamano)
    if (i >= 0) casados.set(indice, libres.splice(i, 1)[0]!)
  }
  return { casados, sinCasar: libres }
}

/** Lo que se enseña de cada fichero: el navegador manda mientras sube; después, el núcleo. */
export const fusionar = (local: FicheroEnPantalla[], nucleo: FicheroDelNucleo[] | undefined): FicheroEnPantalla[] => {
  const porIndice = new Map((nucleo ?? []).map((f) => [f.indice, f]))
  return local.map((f) => {
    const n = porIndice.get(f.indice)
    if (!n) return f
    // Mientras el navegador sube (o reduce), su estado es el bueno; si el núcleo ya sabe algo más, manda él.
    if ((f.estado === 'subiendo' || f.estado === 'reduciendo') && n.estado === 'pendiente') return f
    if (f.estado === 'fallido' && n.estado === 'pendiente') return f // falló al subir: el núcleo ni lo vio
    if (f.estado === 'cancelado') return f
    return { ...f, estado: n.estado, progreso: n.estado === 'pendiente' ? f.progreso : 1, motivo: n.error ?? f.motivo, medio: n.medio, url: n.url }
  })
}

/**
 * La cola del navegador: hasta `aLaVez` subidas a la vez, en orden; **pausar** no corta lo que
 * está subiendo (eso sigue y el núcleo lo procesa), solo deja de empezar lo siguiente; **cancelar**
 * corta lo que está subiendo. Pura en lo que importa: la subida de cada uno la hace `subir`.
 */
export class ColaDeSubida {
  private pendientes: number[]
  private enMarcha = new Map<number, AbortController>()
  private pausada = false
  private cancelada = false
  private alTerminar: null | (() => void) = null

  constructor(
    indices: number[],
    private readonly subir: (indice: number, senal: AbortSignal) => Promise<void>,
    private readonly aLaVez = A_LA_VEZ,
  ) {
    this.pendientes = [...indices]
  }

  /** Arranca y resuelve cuando no queda nada por subir (o se pausa o se cancela y lo que estaba en marcha acaba). */
  empezar(): Promise<void> {
    return new Promise((resolver) => {
      this.alTerminar = resolver
      this.llenar()
    })
  }

  pausar(): void {
    this.pausada = true
  }

  /** Sigue con lo que quedaba, y con lo que se añada (los ficheros elegidos otra vez). */
  reanudar(mas: number[] = []): Promise<void> {
    this.pausada = false
    for (const i of mas) if (!this.pendientes.includes(i) && !this.enMarcha.has(i)) this.pendientes.push(i)
    return this.alTerminar ? Promise.resolve(this.llenar()) : this.empezar()
  }

  cancelar(): void {
    this.cancelada = true
    this.pendientes = []
    for (const c of this.enMarcha.values()) c.abort()
  }

  get quedan(): number[] {
    return [...this.pendientes]
  }

  get subiendo(): number[] {
    return [...this.enMarcha.keys()]
  }

  private llenar(): void {
    while (!this.pausada && !this.cancelada && this.enMarcha.size < this.aLaVez && this.pendientes.length > 0) {
      const indice = this.pendientes.shift()!
      const control = new AbortController()
      this.enMarcha.set(indice, control)
      void this.subir(indice, control.signal)
        .catch(() => undefined)
        .finally(() => {
          this.enMarcha.delete(indice)
          this.llenar()
        })
    }
    if (this.enMarcha.size === 0 && (this.pausada || this.cancelada || this.pendientes.length === 0)) {
      const fin = this.alTerminar
      this.alTerminar = null
      fin?.()
    }
  }
}
